import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useAchievementsContext } from '../../../../contexts/AchievementsContext/AchievementsContext';
import { useApiService } from '../../../../contexts/ApiServiceContext/ApiServiceContext';
import { GAME_PASS_ACHIEVEMENT_NAME } from '../../../../dorian-shared/types/achievement/Achievement';
import { logger } from '../../../../services/loggerService/loggerService';
import { showToast } from '../../../ui/utils';
import { IAchievementModal } from '../AchievementsModalTypes';
import { useAchievementsModalContext } from '../context/AchievementsModalContext';

export function useGamePassActions() {
  const { values } = useFormikContext<IAchievementModal[]>();
  const { setIsFormLoading, isFormLoading, haveValuesChanged } = useAchievementsModalContext();
  const { isLoading, fetchAchievements, bookId } = useAchievementsContext();
  const apiService = useApiService();
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);

  const isGamePassAchievementExisting = useMemo(() => !values.some(
    (v) => v.name.toLowerCase().trim() === GAME_PASS_ACHIEVEMENT_NAME.toLowerCase().trim(),
  ), [values]);
  const isBusy = isFormLoading || isLoading;

  const handleConfirm = useCallback(async () => {
    if (!bookId) {
      showToast({ textMessage: 'Book ID not found', variant: 'danger' });
      return;
    }
    try {
      setIsFormLoading(true);
      const response = await apiService.craeateAndBackfillGamePass(bookId);
      if (response.success) {
        await fetchAchievements();
        showToast({ textMessage: 'Game pass created and backfilled successfully', variant: 'success' });
      } else {
        showToast({ textMessage: `Failed to create game pass${response.message}`, variant: 'danger' });
      }
    } catch (error) {
      showToast({ textMessage: 'Failed to create game pass', variant: 'danger' });
      logger.error(error);
    } finally {
      setIsFormLoading(false);
      setShowDiscardConfirmation(false);
    }
  }, [apiService, bookId, fetchAchievements, setIsFormLoading]);

  const handleClick = useCallback(() => {
    const hasChanged = haveValuesChanged(values);
    if (hasChanged) {
      setShowDiscardConfirmation(true);
    } else {
      handleConfirm();
    }
  }, [haveValuesChanged, values, handleConfirm]);

  const handleCancel = useCallback(() => setShowDiscardConfirmation(false), []);

  return {
    isGamePassAchievementExisting,
    isBusy,
    showDiscardConfirmation,
    handleClick,
    handleConfirm,
    handleCancel,
  };
}
